import { Trash2 } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Adding a question to the contact form without leaving the builder (ACL-338).
 *
 * The question becomes a field of the contact form, which is where every answer lives, so it also
 * shows on the contact record and can be asked by any flow. The builder only ever attaches
 * questions; this is the one place it makes one, and it makes it the same way the Custom fields
 * screen does.
 *
 * An option's key is fixed when it is made and the label is free to change afterwards: rules
 * compare the key, so renaming "Male" never moves a lead onto a different path.
 */

export interface NewQuestion {
  section: string;
  title: string;
  type: string;
  config: { options?: { key: string; label: string }[] };
}

const TYPES = [
  { type: "dropdown", label: "Pick one", hint: "A list of answers, one of which is chosen" },
  { type: "boolean", label: "Yes or no", hint: "Branches on Yes and No" },
  { type: "multi_select", label: "Pick several", hint: "A list where more than one may be chosen" },
  { type: "text", label: "Short text", hint: "Typed in. A branch can only ask whether it was answered" },
  { type: "long_text", label: "Notes", hint: "Several lines. Cannot be branched on" },
  { type: "number", label: "A number", hint: "Can be compared as over or under" },
  { type: "date", label: "A date", hint: "Cannot be branched on yet" },
];

/** An option's key, from its label: lowercase, fixed once made. */
export function optionKeyFrom(label: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const base =
    label
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 36) || "option";
  if (!used.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${base}_${used.size}`;
}

export function NewQuestionDialog({
  open,
  onOpenChange,
  sections,
  saving,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The contact form's sections, so the admin says where the question belongs. */
  sections: { key: string; title: string }[];
  saving: boolean;
  onCreate: (question: NewQuestion) => void;
}) {
  const [title, setTitle] = React.useState("");
  const [type, setType] = React.useState("dropdown");
  const [section, setSection] = React.useState("");
  const [labels, setLabels] = React.useState<string[]>(["", ""]);

  React.useEffect(() => {
    if (!open) return;
    setTitle("");
    setType("dropdown");
    setSection(sections[0]?.key ?? "");
    setLabels(["", ""]);
  }, [open, sections]);

  const wantsOptions = type === "dropdown" || type === "multi_select";
  const filled = labels.map((l) => l.trim()).filter(Boolean);
  const ready =
    title.trim().length > 0 && section.length > 0 && (!wantsOptions || filled.length >= 2);

  function create() {
    const keys: string[] = [];
    const options = filled.map((label) => {
      const key = optionKeyFrom(label, keys);
      keys.push(key);
      return { key, label };
    });
    onCreate({
      section,
      title: title.trim(),
      type,
      config: wantsOptions ? { options } : {},
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DialogHeader>
          <DialogTitle>A new question</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="What to ask" htmlFor="new-title">
            <Input
              id="new-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What is the lead's gender?"
            />
          </Field>

          <Field label="How it is answered" htmlFor="new-type">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="new-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t.type} value={t.type}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <p className="text-xs text-text-muted">{TYPES.find((t) => t.type === type)?.hint}</p>

          {wantsOptions ? (
            <Field label="The answers" htmlFor="new-option-0" hint="At least two. A path can be made for each of them.">
              <div className="space-y-2">
                {labels.map((label, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      id={`new-option-${index}`}
                      value={label}
                      onChange={(e) =>
                        setLabels((old) => old.map((l, i) => (i === index ? e.target.value : l)))
                      }
                      placeholder={index === 0 ? "Male" : index === 1 ? "Female" : "Another answer"}
                    />
                    {labels.length > 2 ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove answer ${index + 1}`}
                        onClick={() => setLabels((old) => old.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setLabels((old) => [...old, ""])}>
                  Add an answer
                </Button>
              </div>
            </Field>
          ) : null}

          <Field label="Where it belongs on the contact record" htmlFor="new-section">
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger id="new-section">
                <SelectValue placeholder="Pick a section" />
              </SelectTrigger>
              <SelectContent>
                {sections.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <p className="text-xs text-text-muted">
            The question is added to the contact form, so it shows on the contact record too and
            any flow can ask it.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!ready} loading={saving} onClick={create}>
            Add the question
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
